import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  TextField,
  Skeleton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';

type Row = {
  id: string;
  componente: string;
  isEditing?: boolean;
  isNew?: boolean;
};

type Props = {
  initialData?: Array<{
    id?: string;
    nome: string;
  }>;
  onAcessoComponentesChange?: (
    acessos: Array<{
      id?: string;
      nome: string;
    }>
  ) => void;
  isLoading?: boolean;
};

const convertToRows = (
  acessoComponentes: Array<{
    id?: string;
    nome: string;
  }>
): Row[] => {
  return acessoComponentes.map((acesso, index) => ({
    // prefer existing id, otherwise create a unique temp id (timestamp + random) to avoid collisions
    id: acesso.id || `temp-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
    componente: acesso.nome,
  }));
};

const convertToAcessoComponentes = (
  rows: Row[]
): Array<{
  id?: string;
  nome: string;
}> => {
  return rows.map((row) => ({
    // Only include id if it's not a temporary one
    ...(row.id.startsWith('temp-') ? {} : { id: row.id }),
    nome: row.componente,
  }));
};

export const ListaAcessoComponentes: React.FC<Props> = ({
  initialData = [],
  onAcessoComponentesChange,
  isLoading = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [editingValues, setEditingValues] = useState<Record<string, string>>(
    {}
  );

  useEffect(() => {
    // Convert initialData to rows format and update state
    const newRows = convertToRows(initialData);
    setRows(newRows);
  }, [initialData]);

  // Reset editing values when the initial data changes to avoid stale editors
  useEffect(() => {
    setEditingValues({});
  }, [initialData]);

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;

    // Set this row as editing and store current value
    setRows(rows.map((r) => (r.id === id ? { ...r, isEditing: true } : r)));
    setEditingValues((prev) => ({ ...prev, [id]: row.componente }));
  };

  const handleCancelEdit = (id: string) => {
    // Cancel editing mode
    setRows(
      rows
        .map((r) => {
          if (r.id === id) {
            if (r.isNew) {
              // Remove new rows that weren't saved
              return null;
            }
            return { ...r, isEditing: false };
          }
          return r;
        })
        .filter(Boolean) as Row[]
    );

    // Clear editing value
    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);
  };

  const handleSaveEdit = (id: string) => {
    const newValue = editingValues[id]?.trim();
    if (!newValue) {
      return; // Don't save empty values
    }


    // Update the row
    const newRows = rows.map((r) =>
      r.id === id
        ? { ...r, componente: newValue, isEditing: false, isNew: false }
        : r
    );

    setRows(newRows);

    // Clear editing value
    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    // Notify parent component
    if (onAcessoComponentesChange) {
      const updatedComponents = convertToAcessoComponentes(newRows);
      onAcessoComponentesChange(updatedComponents);
    }
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);

    // Update local state first
    setRows(newRows);

    // Clear any editing value for this row
    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    // Then notify parent component
    if (onAcessoComponentesChange) {
      const updatedComponents = convertToAcessoComponentes(newRows);
      onAcessoComponentesChange(updatedComponents);
    }
  };

  const handleAddNew = () => {
    const newRow: Row = {
      id: `temp-new-${Date.now()}`,
      componente: '',
      isEditing: true,
      isNew: true,
    };

    setRows([...rows, newRow]);
    setEditingValues((prev) => ({ ...prev, [newRow.id]: '' }));
  };

  const handleEditingValueChange = (id: string, value: string) => {
    setEditingValues({ ...editingValues, [id]: value });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={80} height={36} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ width: '80px' }}
          onClick={handleAddNew}
          startIcon={<AddIcon />}
        >
          Novo
        </Button>
      )}

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Componente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {row.isEditing ? (
                    <>
                      <Tooltip title="Salvar">
                        <IconButton
                          size="small"
                          onClick={() => handleSaveEdit(row.id)}
                          color="primary"
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          onClick={() => handleCancelEdit(row.id)}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(row.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {row.isEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      value={editingValues[row.id] || ''}
                      onChange={(e) =>
                        handleEditingValueChange(row.id, e.target.value)
                      }
                      placeholder="Nome do componente"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSaveEdit(row.id);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          handleCancelEdit(row.id);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    row.componente
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
